import { useState } from 'react';
import { ArrowLeftRight, Copy, Forward, Pencil, Reply, TextCursorInput, Trash2 } from 'lucide-react';
import { canReplyTo, type MessageView } from '../chat/ChatContext.tsx';
import { formatAlbumLabel, formatDuration } from '../lib/format.ts';
import { isSoloSticker, parseMessage, smileyUrl } from '../lib/smileys.ts';
import { useSmileyStats } from '../lib/smileyStats.ts';
import { useKeyboardInset } from '../lib/useKeyboardInset.ts';
import { useIsMobile } from '../lib/useMedia.ts';
import { playableMs } from './MediaBubbles.tsx';
import { SmileyPanel } from './SmileyPanel.tsx';
import { SmileyPicker } from './SmileyPicker.tsx';
import './HoldMenu.css';
import './MessageActions.css';

/** How many smileys the pill offers before «＋» opens the whole set. */
const REACTION_COUNT = 6;

/** One line describing the message, with its smileys spelled out in words. */
function previewOf(message: MessageView): string {
  if (message.kind === 'image') return 'Изображение';
  if (message.kind === 'file') return message.fileName ?? 'Файл';
  // What the group holds, and its caption if it has one — the menu is acting on
  // one message, and both halves say which.
  if (message.kind === 'album') {
    const videos = message.attachments.filter((item) => item.kind === 'video').length;
    const label = formatAlbumLabel(message.attachments.length - videos, videos);
    const caption = message.body?.trim();
    return caption ? `${label} · ${caption}` : label;
  }
  // A recording is described by how long it is: its file name is a UUID, and
  // there is nothing else to quote back at the person who is about to act on it.
  if (message.kind === 'videoNote') return `Кружочек · ${formatDuration(playableMs(message))}`;
  if (message.kind === 'voice') return `Голосовое · ${formatDuration(playableMs(message))}`;

  const text = parseMessage(message.body ?? '')
    .map((part) => (part.type === 'text' ? part.value : `:${part.label}:`))
    .join('')
    .trim();
  return text || 'Смайлик';
}

/**
 * The long-press menu: reactions above, actions below, the message itself at
 * the top so it is clear which one is being acted on.
 *
 * Reactions are offered on the other person's messages only, as the handoff
 * specifies — reacting to your own is a different feature with a different
 * meaning, and it draws none of it.
 *
 * In a bot's chat (`assistant`) the menu is copy and nothing else. A reaction
 * has no audience there — the bot does not read them and no third party is in
 * the room. Editing a sent prompt would not re-ask anything: the bot answers
 * on arrival, and a reworded question it will never read is a lie on screen.
 * «Удалить у всех» is a promise about other people's screens, and there are
 * none; the one real job deletion did here — pruning the bot's memory —
 * belongs to «/сброс», which resets it out loud. And «Ответить» is
 * spelled «Доработать» on the one message where answering does something —
 * the bot's own picture, which carries that button itself.
 */
export function MessageActions({
  message,
  onClose,
  onEdit,
  onReply,
  onForward,
  onSelect,
  onDelete,
  onReact,
  assistant = false,
}: {
  message: MessageView;
  onClose: () => void;
  onEdit: () => void;
  /** The same thing a swipe left does, for a desktop where nobody swipes. */
  onReply: () => void;
  /** «Переслать» — opens the list of chats this message could be copied into. */
  onForward: () => void;
  /**
   * «Выделить текст» — the same handles a hold-and-drag puts on the words, for
   * anyone who does not think to keep dragging, and the only way in on a desktop
   * where there is no press to hold.
   */
  onSelect: () => void;
  onDelete: (scope: 'all' | 'me') => void;
  onReact: (slug: string | null) => void;
  /** True in a bot's chat, which strips everything the bot makes meaningless. */
  assistant?: boolean;
}) {
  const [picking, setPicking] = useState(false);
  const frequent = useSmileyStats();
  const isMobile = useIsMobile();
  const keyboardInset = useKeyboardInset();

  const solo =
    message.kind === 'text' &&
    message.body !== null &&
    isSoloSticker(parseMessage(message.body));
  const mine = message.reactions.find((reaction) => reaction.mine) ?? null;

  const copy = () => {
    // The stored body, codes and all: pasted back into the composer it becomes
    // the same smileys again.
    void navigator.clipboard?.writeText(message.body ?? message.fileName ?? '');
    onClose();
  };

  const react = (slug: string) => {
    onReact(mine?.slug === slug ? null : slug);
    onClose();
  };

  return (
    <div className="hold-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="hold-preview" onClick={(event) => event.stopPropagation()}>
        {previewOf(message)}
      </div>

      {!message.fromMe && !assistant && !picking && (
        <div className="actions-pill" onClick={(event) => event.stopPropagation()}>
          {frequent.slice(0, REACTION_COUNT).map((smiley) => (
            <button
              key={smiley.slug}
              type="button"
              className={`actions-react${mine?.slug === smiley.slug ? ' actions-react-on' : ''}`}
              aria-label={smiley.label}
              aria-pressed={mine?.slug === smiley.slug}
              onClick={() => react(smiley.slug)}
            >
              <img src={smileyUrl(smiley.slug)} alt="" />
            </button>
          ))}
          <button
            type="button"
            className="actions-react actions-react-more"
            aria-label="Другой смайл"
            onClick={() => setPicking(true)}
          >
            ＋
          </button>
        </div>
      )}

      {mine && !assistant && !picking && (
        <button
          type="button"
          className="actions-unreact"
          onClick={(event) => {
            event.stopPropagation();
            onReact(null);
            onClose();
          }}
        >
          Убрать мою реакцию
        </button>
      )}

      {!picking && (
        <div className="hold-menu" onClick={(event) => event.stopPropagation()}>
          {/* First, because it is the one thing here that continues the
              conversation rather than acting on what is already in it. */}
          {canReplyTo(message) && !assistant && (
            <button type="button" className="hold-item" onClick={onReply}>
              <Reply size={18} />
              Ответить
            </button>
          )}

          {/* Second, where the canvas puts it: answering and passing on are the
              two things one does with a message that has just been read, and
              they belong next to each other. Under the same condition as
              «Ответить» — a call is a record rather than something anybody said,
              and a bubble still on its way up has an id only this device knows,
              which the server would not find. */}
          {canReplyTo(message) && !assistant && (
            <button type="button" className="hold-item" onClick={onForward}>
              <Forward size={18} />
              Переслать
            </button>
          )}

          {/* Then the fragment path: it is the other half of what a hold means,
              and the reader who opened this menu instead of dragging is the one
              being told it exists. */}
          {message.kind === 'text' && !solo && (message.body ?? '') !== '' && (
            <button type="button" className="hold-item" onClick={onSelect}>
              <TextCursorInput size={18} />
              Выделить текст
            </button>
          )}

          {message.fromMe && message.kind === 'text' && !assistant && (
            <button type="button" className="hold-item" onClick={onEdit}>
              {solo ? <ArrowLeftRight size={18} /> : <Pencil size={18} />}
              {solo ? 'Заменить смайл' : 'Редактировать'}
            </button>
          )}

          <button type="button" className="hold-item" onClick={copy}>
            <Copy size={18} />
            {message.kind === 'text' && !solo && (message.body ?? '') !== ''
              ? 'Копировать всё'
              : 'Копировать'}
          </button>

          {!assistant && (
            <button
              type="button"
              className="hold-item hold-item-danger"
              onClick={() => onDelete(message.fromMe ? 'all' : 'me')}
            >
              <Trash2 size={18} />
              {message.fromMe ? (solo ? 'Удалить смайл' : 'Удалить у всех') : 'Удалить у меня'}
            </button>
          )}
        </div>
      )}

      {/* «＋»: the whole set, in the keyboard's slot on a phone and as the
          ordinary popover on a desktop, where there is no keyboard to replace. */}
      {picking &&
        (isMobile ? (
          <div className="actions-panel" onClick={(event) => event.stopPropagation()}>
            <SmileyPanel
              height={keyboardInset}
              reacting
              onPick={react}
              onKeyboard={() => setPicking(false)}
              onBackspace={() => setPicking(false)}
              onCancelReaction={() => setPicking(false)}
            />
          </div>
        ) : (
          <div className="actions-picker" onClick={(event) => event.stopPropagation()}>
            <SmileyPicker onPick={react} onClose={() => setPicking(false)} />
          </div>
        ))}
    </div>
  );
}
